import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { API } from "../lib/api";
import BottomTabBar from "./BottomTabBar";
import NotificationBell from "./NotificationBell";
import {
  House, UsersThree, CalendarCheck, ClipboardText,
  ChartLineUp, Gear, SignOut, Airplane, MapPin, List, X, Money, Receipt, UserCircle,
} from "@phosphor-icons/react";

const LOGO_URL = "https://customer-assets-v7afamib.emergentagent.net/job_hr-employee-portal-1/artifacts/16j2gtd7_GREENRGY%20NEW%20FINAL%20LOGO.webp";

const NAV = [
  { to: "/", label: "Dashboard", icon: House, testId: "nav-dashboard" },
  { to: "/attendance", label: "Attendance", icon: CalendarCheck, testId: "nav-attendance" },
  { to: "/reports", label: "Daily Reports", icon: ClipboardText, testId: "nav-reports" },
  { to: "/leaves", label: "Leave Requests", icon: Airplane, testId: "nav-leaves" },
  { to: "/expenses", label: "Expense Claims", icon: Receipt, testId: "nav-expenses" },
  { to: "/payroll", label: "Payroll", icon: Money, testId: "nav-payroll" },
  { to: "/field-map", label: "Field Map", icon: MapPin, adminOnly: true, testId: "nav-field-map" },
  { to: "/employees", label: "Employees", icon: UsersThree, adminOnly: true, testId: "nav-employees" },
  { to: "/finance", label: "Finance", icon: ChartLineUp, adminOnly: true, testId: "nav-finance" },
  { to: "/settings", label: "Settings", icon: Gear, adminOnly: true, testId: "nav-settings" },
  { to: "/profile", label: "My Profile", icon: UserCircle, testId: "nav-profile" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const isAdmin = user?.role === "admin";
  const items = NAV.filter((n) => !n.adminOnly || isAdmin);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] md:flex">
      {/* Mobile top bar */}
      <div className="md:hidden bg-white border-b border-[#E5E7EB] flex items-center justify-between px-4 py-3 sticky top-0 z-30">
        <img src={LOGO_URL} alt="Greenrgy" className="h-8 w-auto object-contain" />
        <div className="flex items-center gap-2">
          <NotificationBell />
          <button onClick={() => setOpen(true)} data-testid="mobile-menu-btn"
                  className="border border-[#E5E7EB] p-2 hover:bg-[#F9FAFB]">
            <List size={20} weight="bold" />
          </button>
        </div>
      </div>

      {/* Backdrop */}
      {open && (
        <div className="md:hidden fixed inset-0 bg-black/50 z-40"
             onClick={() => setOpen(false)} data-testid="mobile-drawer-backdrop" />
      )}

      {/* Sidebar */}
      <aside
        data-testid="sidebar"
        className={`fixed inset-y-0 left-0 w-64 bg-white border-r border-[#E5E7EB] z-50 flex flex-col transform transition-transform duration-300
                    md:sticky md:top-0 md:h-screen md:translate-x-0 md:z-auto
                    ${open ? "translate-x-0" : "-translate-x-full"}`}
      >
        <div className="px-6 py-6 border-b border-[#E5E7EB] flex items-center justify-between">
          <img src={LOGO_URL} alt="Greenrgy" className="h-10 w-auto object-contain" />
          <div className="flex items-center gap-2">
            <div className="hidden md:block"><NotificationBell /></div>
            <button onClick={() => setOpen(false)} className="md:hidden text-[#6B7280]"
                    data-testid="mobile-drawer-close">
              <X size={20} weight="bold" />
            </button>
          </div>
        </div>

        <nav className="flex-1 py-4 overflow-y-auto">
          {items.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={n.testId}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-6 py-3 text-sm font-medium border-l-2 transition-colors duration-200 ${
                    isActive
                      ? "border-[#106109] bg-[#F0F7EF] text-[#106109]"
                      : "border-transparent text-[#374151] hover:bg-[#F9FAFB]"
                  }`
                }
              >
                <Icon size={18} weight="regular" />
                {n.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="border-t border-[#E5E7EB] p-4">
          <div className="mb-3 flex items-center gap-3">
            <SidebarAvatar user={user} />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-[#111827] truncate" data-testid="user-name">{user?.name}</div>
              <div className="text-xs text-[#6B7280] truncate">{user?.employee_id} · {user?.role}</div>
            </div>
          </div>
          <button
            onClick={doLogout}
            data-testid="logout-btn"
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm border border-[#E5E7EB] hover:bg-[#F9FAFB] transition-colors duration-200"
          >
            <SignOut size={16} /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 pb-20 md:pb-0">{children}</main>

      {/* Mobile bottom tab bar */}
      <BottomTabBar />
    </div>
  );
}

function SidebarAvatar({ user }) {
  const src = user?.photo_path && user?.id ? `${API}/employees/${user.id}/photo` : null;
  const initials = (user?.name || "")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase() || "?";
  return (
    <div className="w-10 h-10 rounded-full overflow-hidden bg-[#106109]/10 border border-[#106109]/30 flex items-center justify-center flex-shrink-0" data-testid="sidebar-avatar">
      {src ? (
        <img src={src} alt={user?.name || "avatar"} className="w-full h-full object-cover" />
      ) : (
        <span className="text-xs font-display font-black text-[#106109]">{initials}</span>
      )}
    </div>
  );
}
